import type { Payment, SessionStatusResponse } from '../../types/sessionStatus.types';
import { resolveCreditCardState, type CreditCardState } from '../creditCardStatus';
import type { DigitalWalletPaymentInfo } from '../paymentInfoDisplay/types';

export type DigitalWalletState = CreditCardState;

export type WalletType = DigitalWalletPaymentInfo['walletType'];

export type TransactionType = NonNullable<DigitalWalletPaymentInfo['transactionType']>;

const WALLET_PAYMENT_METHODS = ['credit_card', 'debit_card'];

const WALLET_TYPE_BY_TOKENIZATION: Record<string, WalletType> = {
  apple_pay: 'apple_pay',
  google_pay: 'google_pay',
  android_pay: 'google_pay',
  click_to_pay: 'click_to_pay',
  c2p: 'click_to_pay',
};

export function normalizeWalletType(
  tokenizationMethod?: string
): WalletType | undefined {
  if (!tokenizationMethod) return undefined;

  const normalized = tokenizationMethod
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

  return WALLET_TYPE_BY_TOKENIZATION[normalized];
}

export function isDigitalWalletPayment(payment?: Payment): boolean {
  if (!payment || !WALLET_PAYMENT_METHODS.includes(payment.payment_method)) {
    return false;
  }
  return !!normalizeWalletType(payment.card?.tokenization_method);
}

export function resolveDigitalWalletState(orderStatus?: string): DigitalWalletState {
  return resolveCreditCardState(orderStatus);
}

export function resolveTransactionType(paymentMethod?: string): TransactionType {
  return paymentMethod === 'debit_card' ? 'debit' : 'credit';
}

export function buildDigitalWalletPaymentInfo(
  sessionData: SessionStatusResponse,
  state: DigitalWalletState
): DigitalWalletPaymentInfo | undefined {
  const payment = sessionData?.payments?.[0];
  const walletType = normalizeWalletType(payment?.card?.tokenization_method);

  if (state !== 'approved' || !payment?.card || !walletType) return undefined;

  const totalInCents = sessionData?.summary?.total ?? 0;
  const installments = payment.installments ?? 1;

  return {
    paymentMethod: 'digital_wallet',
    walletType,
    transactionType: resolveTransactionType(payment.payment_method),
    brand: payment.card.brand || undefined,
    lastDigits: payment.card.last_four_digits,
    installments,
    installmentAmount: totalInCents / 100 / (installments || 1),
    customerName: payment.card.holder_name || sessionData?.customer?.name || '',
    customerDocument: sessionData?.customer?.document || '',
  };
}
